import React from "react";
import { useModalForm } from "../../hooks/useModalForm";
import { isValidYouTubeUrl } from "../../utils/youtube";
import { fieldClass, selectClass } from "../fields/fieldStyles";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import TextInput from "../fields/TextInput";
import ModalShell from "../modals/ModalShell";
import Button from "../common/Button";
import {
  Exercise,
  TrainingMethod,
  ExerciseBodyType,
} from "../../types/exercise";
import { logger } from "../../utils/logger";

interface ExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (exerciseData: any) => Promise<void>;
  exercise?: Exercise | null;
  mode: "create" | "edit";
  loading?: boolean;
  error?: string | null;
}

const ExerciseModal: React.FC<ExerciseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  exercise,
  mode,
  loading = false,
  error: responseError,
}) => {
  const {
    values: formData,
    setValue: handleInputChange,
    errors,
    isSubmitting,
    handleSubmit,
  } = useModalForm({
    isOpen,
    initialValues:
      mode === "edit" && exercise
        ? {
            name: exercise.name || "",
            is_at_home: exercise.is_at_home || false,
            is_gym_based: exercise.is_gym_based || false,
            training_method: exercise.training_method || TrainingMethod.WEIGHT,
            type: exercise.type || ExerciseBodyType.UPPER_BODY,
            yt_link: exercise.yt_link || "",
            target_muscle_group: exercise.target_muscle_group || "",
            description: exercise.description || "",
            admin_note: exercise.admin_note || "",
          }
        : {
            name: "",
            is_at_home: false,
            is_gym_based: false,
            training_method: TrainingMethod.WEIGHT,
            type: ExerciseBodyType.UPPER_BODY,
            yt_link: "",
            target_muscle_group: "",
            description: "",
            admin_note: "",
          },
    validate: (v) => {
      const next: Record<string, string> = {};

      if (!v.name || v.name.trim() === "") {
        next.name = "Exercise name is required";
      }

      if (!v.training_method) {
        next.training_method = "Training method is required";
      }

      // utils/youtube.ts owns this; the modal used to carry its own regex.
      if (
        v.yt_link &&
        v.yt_link.trim() &&
        !isValidYouTubeUrl(v.yt_link.trim())
      ) {
        next.yt_link = "YouTube URL must be a valid YouTube video URL";
      }

      if (v.description && v.description.length > 3000) {
        next.description = "Description must not exceed 3000 characters";
      }

      return next;
    },
    onSubmit: async (v) => {
      try {
        await onSubmit({
          name: v.name.trim(),
          is_at_home: v.is_at_home,
          is_gym_based: v.is_gym_based,
          training_method: v.training_method,
          type: v.type,
          yt_link: v.yt_link?.trim() || null,
          target_muscle_group: v.target_muscle_group?.trim() || null,
          description: v.description?.trim() || null,
          admin_note: v.admin_note?.trim() || null,
        });
      } catch (error) {
        logger.error("Error submitting form:", error);
      }
    },
  });

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Create New Exercise" : "Edit Exercise"}
      size="2xl"
      closeDisabled={isSubmitting}
      message={responseError ? { text: responseError, type: "error" } : null}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Saving..."
            disabled={loading}
          >
            {mode === "create" ? "Create Exercise" : "Update Exercise"}
          </Button>
        </>
      }
    >
      <div className="p-6">
        <div className="space-y-4">
          {/* Exercise Name */}
          <div>
            <Label>Exercise Name *</Label>
            <TextInput
              type="text"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              invalid={!!errors.name}
              placeholder="Enter exercise name"
            />
            <FieldError>{errors.name}</FieldError>
          </div>

          {/* Training Method */}
          <div>
            <Label>Training Method *</Label>
            <select
              value={formData.training_method}
              onChange={(e) =>
                handleInputChange(
                  "training_method",
                  e.target.value as TrainingMethod
                )
              }
              className={selectClass(!!errors.training_method)}
            >
              <option value={TrainingMethod.WEIGHT}>Weight</option>
              <option value={TrainingMethod.RESISTANCE_BAND}>
                Resistance Band
              </option>
              <option value={TrainingMethod.TIMED}>Timed</option>
            </select>
            <FieldError>{errors.training_method}</FieldError>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select the primary training method for this exercise
            </p>
          </div>

          {/* Exercise Type */}
          <div>
            <Label>Exercise Type *</Label>
            <select
              value={formData.type || ExerciseBodyType.UPPER_BODY}
              onChange={(e) =>
                handleInputChange("type", e.target.value as ExerciseBodyType)
              }
              className={selectClass()}
            >
              <option value={ExerciseBodyType.UPPER_BODY}>
                {ExerciseBodyType.UPPER_BODY}
              </option>
              <option value={ExerciseBodyType.LOWER_BODY}>
                {ExerciseBodyType.LOWER_BODY}
              </option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select the body region targeted by this exercise
            </p>
          </div>

          {/* Location Checkboxes */}
          <div>
            <Label>Exercise Location</Label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_at_home}
                  onChange={(e) =>
                    handleInputChange("is_at_home", e.target.checked)
                  }
                  className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Can be done at home
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_gym_based}
                  onChange={(e) =>
                    handleInputChange("is_gym_based", e.target.checked)
                  }
                  className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Requires gym equipment
                </span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              You can select both options if the exercise can be adapted for
              either location
            </p>
          </div>

          {/* YouTube Link */}
          <div>
            <Label>YouTube Video Link</Label>
            <TextInput
              type="url"
              value={formData.yt_link || ""}
              onChange={(e) => handleInputChange("yt_link", e.target.value)}
              invalid={!!errors.yt_link}
              placeholder="https://www.youtube.com/watch?v=... (optional)"
            />
            <FieldError>{errors.yt_link}</FieldError>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Link to a YouTube video demonstrating the exercise
            </p>
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <textarea
              rows={4}
              value={formData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              maxLength={3000}
              className={fieldClass(!!errors.description)}
              placeholder="Enter exercise description, instructions, or notes (optional)"
            />
            <FieldError>{errors.description}</FieldError>
          </div>

          {/* Admin Notes */}
          <div>
            <Label>Admin Notes</Label>
            <textarea
              rows={3}
              value={formData.admin_note || ""}
              onChange={(e) => handleInputChange("admin_note", e.target.value)}
              className={fieldClass(!!errors.admin_note)}
              placeholder="Internal notes for admins only (optional)"
            />
            <FieldError>{errors.admin_note}</FieldError>
          </div>

          {/* YouTube Video Preview */}
          {formData.yt_link && isValidYouTubeUrl(formData.yt_link.trim()) && (
            <div>
              <Label>Video Preview</Label>
              <div className="rounded-lg bg-gray-100 p-3 dark:bg-navy-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  ✓ Valid YouTube URL provided
                </p>
                <a
                  href={formData.yt_link.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-biafit-primary hover:text-biafit-secondary"
                >
                  View video on YouTube →
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default ExerciseModal;
